// 📈 마케팅 유입경로 수집/보존 유틸
// - 첫 진입 시 utm_source/ref 파라미터 또는 referrer를 읽어 localStorage에 보존
// - 본인인증(PortOne) 전체 페이지 리다이렉트를 거쳐도 유지되어야 하므로 sessionStorage 불가
// - 신청 제출 시 readStoredAttribution()으로 읽어 applications 문서의 inflow 필드로 저장

import { normalizeInflowSource, resolveSourceFromReferrer } from '../constants/inflowChannels';

export interface InflowAttribution {
  source: string;        // 정규화된 utm_source/ref 값 또는 referrer 매핑 채널
  medium?: string;       // utm_medium (예: banner)
  campaign?: string;     // utm_campaign
  content?: string;      // utm_content (배너 위치 등)
  referrer?: string;     // 유입 referrer 호스트명
  landing_page?: string; // 최초 착지 경로
  captured_at: string;   // 수집 시각 (ISO)
}

const STORAGE_KEY = 'dwt_inflow_attribution';
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30일 어트리뷰션 윈도우

/**
 * 저장된 유입 정보 읽기 (만료/손상 시 삭제 후 null)
 */
export function readStoredAttribution(): InflowAttribution | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as InflowAttribution;
    if (!parsed || typeof parsed.source !== 'string' || !parsed.source) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    const capturedAt = Date.parse(parsed.captured_at ?? '');
    if (Number.isNaN(capturedAt) || Date.now() - capturedAt > TTL_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    // localStorage 접근 불가(프라이빗 모드 등) 또는 JSON 손상 — 유입 수집만 포기
    return null;
  }
}

function saveAttribution(attr: InflowAttribution): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(attr));
  } catch {
    // 저장 실패해도 신청 흐름에는 영향 없음
  }
}

/**
 * 진입 시 유입 정보 수집 (멱등 — StrictMode 2회 실행 안전)
 * 우선순위:
 *   1. utm_source/ref 파라미터 있음 → 항상 덮어쓰기 (마지막 캠페인 클릭 우선)
 *   2. 저장된 값 있음 → 유지 (본인인증 복귀 시 PG 도메인이 referral로 오염되는 것 방지)
 *   3. 외부 referrer → 채널 매핑 또는 'referral'
 *   4. 아무 정보 없음 → 'direct'
 */
export function captureAttribution(
  search: string,
  referrer: string,
  pathname: string,
  hostname: string,
): void {
  try {
    const params = new URLSearchParams(search);
    const rawSource = params.get('utm_source') ?? params.get('ref') ?? '';
    const source = rawSource.trim() ? normalizeInflowSource(rawSource) : '';

    if (source) {
      saveAttribution({
        source,
        medium: params.get('utm_medium')?.trim() || undefined,
        campaign: params.get('utm_campaign')?.trim() || undefined,
        content: params.get('utm_content')?.trim() || undefined,
        referrer: getReferrerHost(referrer, hostname) ?? undefined,
        landing_page: pathname,
        captured_at: new Date().toISOString(),
      });
      return;
    }

    if (readStoredAttribution()) return;

    const referrerHost = getReferrerHost(referrer, hostname);
    if (referrerHost) {
      saveAttribution({
        source: resolveSourceFromReferrer(referrerHost) ?? 'referral',
        medium: 'referral',
        referrer: referrerHost,
        landing_page: pathname,
        captured_at: new Date().toISOString(),
      });
      return;
    }

    saveAttribution({
      source: 'direct',
      landing_page: pathname,
      captured_at: new Date().toISOString(),
    });
  } catch {
    // 어떤 예외도 사용자 흐름을 막지 않음
  }
}

/** 외부 referrer의 호스트명 (없거나 same-origin이면 null) */
function getReferrerHost(referrer: string, currentHostname: string): string | null {
  if (!referrer) return null;
  try {
    const host = new URL(referrer).hostname;
    if (!host || host === currentHostname) return null;
    return host;
  } catch {
    return null;
  }
}

/**
 * Firestore 저장용 객체 변환 — undefined 키 제거
 * (firebase.ts가 ignoreUndefinedProperties를 설정하지 않아 undefined 포함 시 addDoc이 throw)
 */
export function toFirestoreInflow(attr: InflowAttribution): Record<string, string> {
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(attr)) {
    if (typeof value === 'string' && value) {
      result[key] = value;
    }
  }
  return result;
}
